import React, { useCallback, useMemo } from 'react'
import { OnboardingStepKey } from '@cbgms/api'
import { SignUpForm } from 'app/auth/components/onboarding/steps/SignupCredentialsStep/SignUpForm'
import { stepOutputProps } from 'app/auth/components/onboarding/steps/SignupCredentialsStep/use-signup-credentials-step'
import InviteUserToOtherShop from 'app/auth/components/onboarding/steps/shared/InviteUserToOtherShop'
import { useOnboardingContext } from 'app/auth/components/onboarding/use-onboarding-context'
import { Redirect, Route, Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom'

const credentialsStepPath = 'get-credentials'

const WrappedSignupCredentialsStep: React.FC<{ onSubmitSuccess?: () => void }> = ({ onSubmitSuccess = () => {} }) => {
  const { steps, basePath } = useOnboardingContext()
  const h = useHistory()
  const step = steps.find(a => a.step === OnboardingStepKey.SignUpCredentials)
  if (!step) {
    return <div>invalid onboarding state</div>
  }

  return (
    <SignUpForm
      {...step.stepProps}
      isPersisting={false}
      isFirstStep={false}
      hideSkip={step.hideSkip}
      shouldRunInBackground={step.shouldRunInBackground}
      initialData={step.stepData.InitialData}
      duplicationCheck={step.stepData.DuplicationCheck}
      basePath={basePath}
      onSubmitSuccess={onSubmitSuccess}
      prevStep={() => h.replace(basePath)}
    />
  )
}

const useShouldRedirectToCredentialStep = () => {
  const { steps } = useOnboardingContext()
  const { pathname } = useLocation()
  const userCredentials = useMemo(() => {
    return (steps.find(s => s.step === OnboardingStepKey.SignUpCredentials) as stepOutputProps | undefined)?.userCredentials
  }, [steps])

  const hasCredentials = !!userCredentials
  const isOnCredentialsStep = pathname.indexOf(credentialsStepPath) !== -1
  return !hasCredentials && !isOnCredentialsStep
}

export const InviteUserToOtherShopWithoutLoginCredentials: React.FC = () => {
  // we've already reached a duplicate shop state but don't possibly don't have the credentials yet.
  // we will render the credentials step and then navigate to the duplicate shop stop
  const { path, url } = useRouteMatch()
  const h = useHistory()
  const shouldRedirectToGetCredentials = useShouldRedirectToCredentialStep()
  const navigateBackToInviteToShop = useCallback(() => h.push(path), [h.push, path])
  if (shouldRedirectToGetCredentials) {
    return <Redirect to={`${url}/${credentialsStepPath}`} />
  }

  return (
    <div>
      <Switch>
        <Route
          path={`${path}/${credentialsStepPath}`}
          render={() => <WrappedSignupCredentialsStep onSubmitSuccess={navigateBackToInviteToShop} />}
        />
        <Route component={InviteUserToOtherShop} />
      </Switch>
    </div>
  )
}
